import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coins, Diamond, Clock, Trophy } from 'lucide-react';
import { getCurrentSeason, getSeasonRewardTiers, getSeasonTimeRemaining, type RewardTier } from '@/lib/seasonal-rankings';

interface SeasonalRewardsCardProps {
  userRank?: number;
}

export function SeasonalRewardsCard({ userRank }: SeasonalRewardsCardProps) {
  const [timeRemaining, setTimeRemaining] = useState(getSeasonTimeRemaining());
  const season = getCurrentSeason();
  const rewardTiers = getSeasonRewardTiers();
  
  // Update countdown every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(getSeasonTimeRemaining());
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Find user's current tier
  const getUserTier = (): RewardTier | null => {
    if (!userRank) return null;
    
    if (userRank === 1) return rewardTiers[0];
    if (userRank <= 3) return rewardTiers[1];
    if (userRank <= 10) return rewardTiers[2];
    if (userRank <= 25) return rewardTiers[3];
    if (userRank <= 50) return rewardTiers[4];
    if (userRank <= 100) return rewardTiers[5];
    return null;
  };
  
  const userTier = getUserTier();
  
  return (
    <Card variant="glass" className="border-primary/20 mb-6 overflow-hidden">
      {/* Season header with gradient */}
      <div className="bg-gradient-to-r from-primary/20 via-teen-purple/20 to-teen-pink/20 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-teen-gold" />
            <div>
              <h2 className="text-xl font-bold">{season.name}</h2>
              <p className="text-sm text-muted-foreground">Season {season.number}</p>
            </div>
          </div>
          
          {/* Countdown timer */}
          <div className="flex items-center gap-2 bg-background/50 rounded-lg px-4 py-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m remaining
            </span>
          </div>
        </div>
      </div>
      
      <CardContent className="pt-4">
        {/* User's current tier (if ranked) */}
        {userRank && userTier && (
          <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-teen-green/20 to-teen-blue/20 border border-teen-green/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{userTier.icon}</span>
                <div>
                  <p className="font-semibold">Your Rank: #{userRank}</p>
                  <p className="text-sm text-muted-foreground">{userTier.name} Tier</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Coins className="w-4 h-4 text-yellow-500" />
                  <span className="font-semibold">{userTier.coins}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Diamond className="w-4 h-4 text-blue-500" />
                  <span className="font-semibold">{userTier.gems}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Reward tiers preview */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Season End Rewards</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {rewardTiers.slice(0, 6).map((tier) => (
              <div 
                key={tier.name}
                className={`p-2 rounded-lg bg-gradient-to-r ${tier.color} bg-opacity-10 border border-border/50 ${
                  userTier?.name === tier.name ? 'ring-2 ring-primary' : ''
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{tier.icon}</span>
                  <span className="text-xs font-semibold">{tier.name}</span>
                </div>
                <p className="text-xs text-muted-foreground">{tier.rank}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs py-0 px-1">
                    <Coins className="w-3 h-3 mr-1" />{tier.coins}
                  </Badge>
                  <Badge variant="outline" className="text-xs py-0 px-1">
                    <Diamond className="w-3 h-3 mr-1" />{tier.gems}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
